(() => {
  if (window.CAJShippingProgressV1) return;
  window.CAJShippingProgressV1 = true;

  let lastTotal = 0;
  let observer = null;
  let observerFrame = 0;
  let cartSubscriptionBound = false;
  let subscriptionAttempts = 0;

  const ROOT_SELECTOR = '[data-caj-shipping-progress]';

  const readConfig = () => {
    const node = document.getElementById('CAJShippingProgressConfig');
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      config.threshold = Math.max(0, Number(config.threshold) || 0);
      config.currency = String(config.currency || 'EGP');
      config.locale = String(config.locale || document.documentElement.lang || 'en');
      config.showDrawer = config.showDrawer !== false;
      config.showCart = config.showCart !== false;
      config.heading = String(config.heading || 'FREE SHIPPING');
      config.remainingMessage = String(config.remainingMessage || 'Add [amount] more to unlock free shipping');
      config.successMessage = String(config.successMessage || 'Free shipping unlocked');
      return config.threshold > 0 ? config : null;
    } catch (error) {
      return null;
    }
  };

  const formatMoney = (cents, config) => {
    const value = Math.max(0, Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(config.locale || undefined, {
        style: 'currency',
        currency: config.currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      return `${value.toLocaleString()} ${config.currency}`;
    }
  };

  const removeRoots = (context) => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!context || root.dataset.cajShippingProgress === context) root.remove();
    });
  };

  const createRoot = (context) => {
    const root = document.createElement('section');
    root.className = `caj-shipping-progress caj-shipping-progress--${context}`;
    root.dataset.cajShippingProgress = context;
    root.setAttribute('aria-label', 'Free shipping progress');
    root.innerHTML = `
      <div class="caj-shipping-progress__heading">
        <span class="caj-shipping-progress__heading-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M3 6.5h11v10H3z"></path><path d="M14 10h3.5l3.5 3.5v3H14z"></path><path d="M6.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path></svg>
        </span>
        <span data-caj-shipping-heading></span>
      </div>
      <p class="caj-shipping-progress__message" data-caj-shipping-message aria-live="polite"></p>
      <div class="caj-shipping-progress__track" data-caj-shipping-track role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <span class="caj-shipping-progress__fill" aria-hidden="true"></span>
        <span class="caj-shipping-progress__goal" aria-hidden="true">
          <svg class="caj-shipping-progress__goal-truck" viewBox="0 0 24 24"><path d="M3 7h11v9H3z"></path><path d="M14 10h3l4 4v2h-7z"></path><path d="M6 18a1.7 1.7 0 1 0 0-3.4A1.7 1.7 0 0 0 6 18ZM18 18a1.7 1.7 0 1 0 0-3.4A1.7 1.7 0 0 0 18 18Z"></path></svg>
          <svg class="caj-shipping-progress__goal-check" viewBox="0 0 24 24"><path d="m5 12.5 4.3 4.3L19 7"></path></svg>
        </span>
      </div>
    `;
    return root;
  };

  const mountRoots = (config) => {
    if (config.showDrawer) {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      const drawerHeader = drawerInner?.querySelector('.drawer__header');
      const existingDrawer = drawerInner?.querySelector('[data-caj-shipping-progress="drawer"]');
      if (drawerInner && drawerHeader && !existingDrawer) {
        drawerHeader.insertAdjacentElement('afterend', createRoot('drawer'));
      }
    } else {
      removeRoots('drawer');
    }

    if (config.showCart) {
      const cartItems = document.querySelector('cart-items');
      const cartTitle = cartItems?.querySelector('.title-wrapper-with-link');
      const existingPage = cartItems?.querySelector('[data-caj-shipping-progress="page"]');
      if (cartItems && cartTitle && !existingPage) {
        cartTitle.insertAdjacentElement('afterend', createRoot('page'));
      }
    } else {
      removeRoots('page');
    }
  };

  const renderRoot = (root, total, config) => {
    const threshold = config.threshold;
    const safeTotal = Math.max(0, Number(total) || 0);
    const complete = safeTotal >= threshold;
    const remaining = Math.max(0, threshold - safeTotal);
    const percent = Math.max(0, Math.min(100, threshold ? (safeTotal / threshold) * 100 : 0));

    root.hidden = safeTotal <= 0;
    root.classList.toggle('is-complete', complete);
    root.style.setProperty('--caj-shipping-progress-value', `${percent}%`);

    const heading = root.querySelector('[data-caj-shipping-heading]');
    const message = root.querySelector('[data-caj-shipping-message]');
    const track = root.querySelector('[data-caj-shipping-track]');

    if (heading) heading.textContent = config.heading;
    if (message) {
      message.textContent = complete
        ? config.successMessage
        : config.remainingMessage.replace('[amount]', formatMoney(remaining, config));
    }
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(percent)));
      track.setAttribute(
        'aria-valuetext',
        complete ? config.successMessage : `${formatMoney(remaining, config)} remaining for free shipping`
      );
    }
  };

  const renderAll = (total = lastTotal) => {
    const config = readConfig();
    if (!config) {
      removeRoots();
      return;
    }

    lastTotal = Math.max(0, Number(total) || 0);
    mountRoots(config);
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => renderRoot(root, lastTotal, config));
  };

  const fetchCart = async () => {
    const config = readConfig();
    if (!config) {
      removeRoots();
      return;
    }

    const base = window.routes?.cart_url || '/cart';
    const url = base.endsWith('.js') ? base : `${base}.js`;

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      lastTotal = Math.max(0, Number(cart.total_price) || 0);
      renderAll(lastTotal);
    } catch (error) {
      renderAll(lastTotal);
    }
  };

  const bindCartUpdates = () => {
    if (cartSubscriptionBound) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const total = Number(event?.cartData?.total_price);
        if (Number.isFinite(total)) {
          lastTotal = Math.max(0, total);
          renderAll(lastTotal);
        } else {
          window.setTimeout(fetchCart, 0);
        }
      });
      cartSubscriptionBound = true;
      return;
    }

    if (subscriptionAttempts < 12) {
      subscriptionAttempts += 1;
      window.setTimeout(bindCartUpdates, 250);
    }
  };

  const startObserver = () => {
    if (observer || !document.body) return;

    observer = new MutationObserver(() => {
      if (observerFrame) return;
      observerFrame = window.requestAnimationFrame(() => {
        observerFrame = 0;
        renderAll(lastTotal);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const refreshFromEditor = () => {
    window.setTimeout(() => {
      bindCartUpdates();
      fetchCart();
    }, 0);
  };

  const init = () => {
    bindCartUpdates();
    startObserver();
    fetchCart();

    document.addEventListener('shopify:section:load', refreshFromEditor);
    document.addEventListener('shopify:section:unload', refreshFromEditor);
    window.addEventListener('pageshow', fetchCart);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
